import type React from "react";
import {useMemo, useState} from "react";
import {Skeleton, Stack, Typography} from "@mui/material";
import type {EChartsType} from "echarts/types/dist/shared";
import {CustomCard} from "~/components/Card/Card";
import {MithraEchart} from "~/services/echarts/MithraEchart";
import type {EChartContextType} from "~/services/echarts/EChartProvider";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {findAbbreviationOfGroup, toCurrency} from "~/components/currency-component/CurrencyClasses";
import {COLORS} from "~/style/colors-ts";
import {useStores} from "~/stores";
import {useSupplierSpendSummaryQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierSpendSummaryQuery";
import {
    SUPPLIER_SPEND_VIEW,
    SUPPLIER_SPEND_VIEW_LABEL,
    SUPPLIER_SPEND_VIEWS,
    type SupplierSpendSummary,
    type SupplierSpendSummaryRow,
    type SupplierSpendView,
} from "~/pages/supplier-360/types/Supplier360Types";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";

const ROW_HEIGHT = 34;
const MIN_CHART_HEIGHT = 120;
/**
 * The names get a fixed, left-aligned column: sized by ECharts' own text measurement (`containLabel`),
 * a name measured narrower than it renders was pushed off the left edge ("asic-Fit International BV").
 * Here a long name is cut at its end with an ellipsis instead.
 */
const LABEL_WIDTH = 150;
const LABEL_GAP = 8;

/**
 * The chart is initialised once, but the row count changes when the view is switched, so the
 * height is fixed to the widest breakdown — otherwise bars would stretch per view.
 */
function chartHeight(summary: SupplierSpendSummary): number {
    const maxRows = Math.max(...SUPPLIER_SPEND_VIEWS.map(v => summary.views[v]?.length ?? 0));
    return Math.max(MIN_CHART_HEIGHT, maxRows * ROW_HEIGHT);
}

function buildSummaryOption(rows: SupplierSpendSummaryRow[], currencySymbol: string) {
    // One shared abbreviation across every label, so the bars don't mix k and M
    const abbreviation = findAbbreviationOfGroup([...rows.map(r => r.spend), 0]);
    const format = (v: number) => toCurrency(currencySymbol, v, 3, abbreviation);
    const maxSpend = Math.max(...rows.map(r => r.spend), 0);

    return {
        tooltip: {
            trigger: 'item',
            valueFormatter: (v: number) => format(Number(v)),
        },
        grid: {top: 4, left: LABEL_WIDTH + LABEL_GAP, right: 72, bottom: 4, containLabel: false},
        xAxis: {
            type: 'value',
            show: false,
            max: maxSpend,
        },
        yAxis: {
            type: 'category',
            // Biggest spend first — the endpoint already orders descending, and ECharts draws
            // the first category at the bottom unless inverted
            inverse: true,
            data: rows.map(r => r.name),
            axisTick: {show: false},
            axisLine: {show: false},
            axisLabel: {
                fontSize: 11.5,
                width: LABEL_WIDTH,
                overflow: 'truncate',
                align: 'left',
                margin: LABEL_WIDTH + LABEL_GAP - 4,
                color: (_v: string, i: number) => rows[i]?.muted ? COLORS.grey[500] : COLORS.grey[800],
            },
        },
        series: [
            {
                // Full-width track — labels sit at the end of this, so every value lines up
                type: 'bar',
                barMaxWidth: 9,
                silent: true,
                tooltip: {show: false},
                data: rows.map(r => ({
                    value: maxSpend,
                    label: {color: r.muted ? COLORS.grey[500] : COLORS.grey[800]},
                })),
                itemStyle: {
                    color: COLORS.grey[100],
                    borderRadius: 4,
                },
                label: {
                    show: true,
                    position: 'right',
                    formatter: ({dataIndex}: {dataIndex: number}) => format(rows[dataIndex].spend),
                    fontSize: 11.5,
                },
                z: 1,
            },
            {
                name: 'Spend',
                type: 'bar',
                barMaxWidth: 9,
                barGap: '-100%',
                data: rows.map(r => ({
                    value: r.spend,
                    itemStyle: {
                        color: r.muted ? COLORS.grey[300] : COLORS.info.main,
                        borderRadius: 4,
                    },
                })),
                z: 2,
            },
        ],
    };
}

function initSummaryChart(
    echart: EChartContextType,
    root: HTMLElement,
    rows: SupplierSpendSummaryRow[],
    height: number,
    currencySymbol: string,
): EChartsType {
    const chart = echart.init(root, 'mithra', {renderer: 'svg', height});
    chart.setOption(buildSummaryOption(rows, currencySymbol));
    return chart;
}


/** Which breakdown opens first: business unit, then the switch's own order. */
const DEFAULT_VIEW_ORDER: readonly SupplierSpendView[] = [
    SUPPLIER_SPEND_VIEW.businessUnit,
    ...SUPPLIER_SPEND_VIEWS.filter(v => v !== SUPPLIER_SPEND_VIEW.businessUnit),
];

export const SpendSummarySection: React.FC<{entityId: number}> = ({entityId}) => {
    const {p} = useStores();
    const {data: summary, isLoading} = useSupplierSpendSummaryQuery(entityId);
    const [pickedView, setPickedView] = useState<SupplierSpendView | null>(null);

    // Until the user picks one, show the first breakdown that actually has rows — a supplier
    // whose parts carry no business unit would otherwise open on an empty chart. "Business unit"
    // is the preferred default whenever it has data; the switch keeps its own order.
    const view = pickedView
        ?? DEFAULT_VIEW_ORDER.find(v => (summary?.views?.[v]?.length ?? 0) > 0)
        ?? SUPPLIER_SPEND_VIEW.businessUnit;

    const rows = useMemo(() => summary?.views?.[view] ?? [], [summary, view]);
    const chartData = useMemo(() => rows.length > 0 ? [rows] : [], [rows]);
    const height = summary ? chartHeight(summary) : MIN_CHART_HEIGHT;

    return (
        <CustomCard title="Spend summary" subHeader="Resolved" sx={{flex: 1, minWidth: 0}}>
            {isLoading && <Skeleton variant="rectangular" height={MIN_CHART_HEIGHT + 40} />}

            {!isLoading && !summary && (
                <Typography variant="body2" color="text.secondary">No spend data available yet.</Typography>
            )}

            {summary && (
                <>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{mb: 1}}>
                        <Typography variant="h6" fontWeight={700}>
                            <CurrencyComponent v={summary.total} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {`by ${SUPPLIER_SPEND_VIEW_LABEL[view].toLowerCase()}`}
                        </Typography>
                        <SegmentedControl
                            labels={SUPPLIER_SPEND_VIEWS.map(v => SUPPLIER_SPEND_VIEW_LABEL[v])}
                            value={SUPPLIER_SPEND_VIEWS.indexOf(view)}
                            onChange={index => setPickedView(SUPPLIER_SPEND_VIEWS[index])}
                            sx={{ml: 'auto'}}
                        />
                    </Stack>

                    {rows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {`No spend attributed to a ${SUPPLIER_SPEND_VIEW_LABEL[view].toLowerCase()}.`}
                        </Typography>
                    ) : (
                        <MithraEchart
                            initChart={(echart, root) => initSummaryChart(echart, root, rows, height, p.currencySymbol)}
                            updateChart={(chart, data) => {
                                const next = data[0] as SupplierSpendSummaryRow[] | undefined;
                                if (next) chart.setOption(buildSummaryOption(next, p.currencySymbol), {notMerge: true});
                            }}
                            data={chartData}
                        />
                    )}
                </>
            )}
        </CustomCard>
    );
};
